import {chain} from 'lodash';
import {connect} from 'react-redux';

import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {DelegationModel} from '../../../../models/api/delegationModel';
import {CollapsedItemKeyPrefixesEnum} from '../../../../models/api/localPreferencesModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplayChevronActionsEnum,
  WorkspaceDisplayChevronSectionsEnum,
  WorkspaceDisplaySectionTrackingTypesEnum,
} from '../../../../services/tracking';
import {AppState} from '../../../../store/appState';
import {getInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {toggleExpandItemAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getFindIsExpanded} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {getShouldAlphabetizeWorkspace} from '../../../../store/data/preferences/preferencesSelectors';
import {getFindTeammate} from '../../../../store/data/teammates/lazyTeammatesSelectors';
import {makeGetOnlineStatus} from '../../../../store/data/teammates/onlineTeammatesSelectors';
import {reorderWorkspaceDelegationAsyncAction} from '../../../../store/data/workspace/workspaceActions';
import {canDropWorkspaceItemAtTarget} from '../dnd/workspaceDragDropHelpers';
import {
  WorkspaceDragItem,
  WorkspaceDragSourceTypesEnum,
  WorkspaceItemDropHandler,
} from '../dnd/workspaceDragDropTypes';
import {DelegationWorkspace} from './delegationWorkspace';

/*
 * Constants.
 */

const defaultIsTeammateWorkspaceExpanded = true;

/*
 * Props.
 */

interface DelegationWorkspaceContainerProps {
  delegation: DelegationModel;
  workspaceDragItem?: WorkspaceDragItem;
}

/*
 * Selectors.
 */

function getDelegation(_state: AppState, props: DelegationWorkspaceContainerProps) {
  return props.delegation;
}

function getOwnerId(_state: AppState, props: DelegationWorkspaceContainerProps) {
  return props.delegation.ownerId;
}

function makeGetOwner() {
  return createSelector([getDelegation, getFindTeammate], (delegation, findTeammate) => {
    const owner = findTeammate(delegation.ownerId, 'DelegationWorkspaceContainer makeGetOwner');
    if (!owner) {
      throw new Error('An owner could not be found for this delegation.');
    }

    return owner;
  });
}

function makeGetOwnerInboxes() {
  return createSelector([getDelegation, getInboxes], (delegation, inboxes) =>
    chain(delegation.inboxIds)
      .map((id) => inboxes[id])
      .compact()
      .value(),
  );
}

function buildTeammateWorkspaceExpandedKey(userNamespace: string) {
  return `${CollapsedItemKeyPrefixesEnum.WORKSPACE}${userNamespace}`;
}

function makeGetIsExpanded(getOwner: ReturnType<typeof makeGetOwner>) {
  return createSelector([getOwner, getFindIsExpanded], (owner, findIsExpanded) =>
    findIsExpanded(buildTeammateWorkspaceExpandedKey(owner.namespace), defaultIsTeammateWorkspaceExpanded),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<DelegationWorkspaceContainerProps>()(() => {
  const getOwner = makeGetOwner();
  return {
    ownerInboxes: makeGetOwnerInboxes(),
    owner: getOwner,
    ownerOnlineStatus: makeGetOnlineStatus(getOwnerId),
    isExpanded: makeGetIsExpanded(getOwner),
    shouldAlphabetizeWorkspace: getShouldAlphabetizeWorkspace,
  };
});

export const DelegationWorkspaceContainer = connect(
  mapStateToProps,
  {
    toggleExpandItem: toggleExpandItemAction,
    reorderWorkspaceDelegation: reorderWorkspaceDelegationAsyncAction,
  },
  (stateProps, dispatchProps, ownProps) => {
    const {owner} = stateProps;

    const onToggleExpandClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CHEVRON_CLICK, {
        location: WorkspaceDisplayChevronSectionsEnum.DELEGATED_SECTION,
        action: stateProps.isExpanded
          ? WorkspaceDisplayChevronActionsEnum.COLLAPSE
          : WorkspaceDisplayChevronActionsEnum.EXPAND,
      });
      dispatchProps.toggleExpandItem(
        buildTeammateWorkspaceExpandedKey(owner.namespace),
        defaultIsTeammateWorkspaceExpanded,
      );
    };

    const onWorkspaceItemDrop: WorkspaceItemDropHandler = (droppedItem, linePosition, target) => {
      if (!target || !canDropWorkspaceItemAtTarget(droppedItem, target)) {
        return;
      }

      if (
        droppedItem.type !== WorkspaceDragSourceTypesEnum.DELEGATION ||
        target.type !== WorkspaceDragSourceTypesEnum.DELEGATION
      ) {
        return;
      }

      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_REORDER, {
        section: WorkspaceDisplaySectionTrackingTypesEnum.DELEGATED_INBOX,
      });
      dispatchProps.reorderWorkspaceDelegation(droppedItem.id, target.id, linePosition);
    };

    return {
      ...stateProps,
      ...ownProps,
      onToggleExpandClick,
      onWorkspaceItemDrop,
    };
  },
)(DelegationWorkspace);
